import React, { useState, useEffect } from "react";
import {
    Button,
    Form,
    Input,
    App,
    Select,
    Spin,
} from "antd";
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

// API
import {
    addUser,
    checkIsEmailExist,
    signInUser,
    getAllCenters,
} from "../../Constants/Api/functions/function";

// Router
import { Link, useHistory } from 'react-router-dom';

// Reducers
import { setUserProfile } from '../../Reducers/userProfileReducer';

// Assets
import signinBg from '../../Assets/Images/signinBg.png'
import signin from '../../Assets/Images/signin.png'

import './style.scss'

const RegisterContent = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [centers, setCenters] = useState([])
    const [centersLoading, setCentersLoading] = useState(false)
    const dispatch = useDispatch();
    const navigate = useHistory();
    const { modal } = App.useApp();

    useEffect(() => {
        handleGetAllCenters();
    }, []);

    const handleGetAllCenters = async () => {
        try {
            setCentersLoading(true);
            const response = await getAllCenters();
            // getAllCenters() already returns response.data (the array), not the full response object
            if (response && Array.isArray(response)) {
                setCenters(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                // Fallback in case the response structure is different
                setCenters(response.data);
            }
        } catch (error) {
            console.error('Error fetching centers:', error);
        } finally {
            setCentersLoading(false);
        }
    };

    const checkEmail = async (email) => {
        const result = await checkIsEmailExist(email);
        // checkIsEmailExist returns { exists: false } or { exists: true }
        return result && result.exists === true;
    }

    const onFinish = async (values) => {
        const isEmailExist = await checkEmail(values.email);

        if (isEmailExist) {
            emailAlreadyExistsModal()
        } else {
            const user = {
                name: values.name,
                email: values.email,
                address: values.address,
                password: values.password,
                role: 'student',
                contactNumber: values.contactNumber,
                centerId: values.centerId || null,
            }

            await handleUserRegister(user, values)
        }
    };

    const handleUserRegister = async (user, values) => {
        try {
            setIsLoading(true)
            const response = await addUser(user)

            // Check if registration was successful (status 200 or 201)
            if (response && (response.status === 200 || response.status === 201)) {
                // Auto login after registration
                try {
                    const loginResponse = await signInUser({ email: values.email, password: values.password });
                    if (loginResponse && loginResponse.status === 200) {
                        // Backend returns { success: true, message: '...', user: {...} }
                        const userData = loginResponse.data.user || loginResponse.data;
                        if (userData && userData.id) {
                            dispatch(setUserProfile(userData));
                            localStorage.setItem('digital-academy-user', JSON.stringify(userData));
                            // Navigate to dashboard
                            navigate.push('/dashboard/home');
                        } else {
                            // If user data is invalid, show success modal and redirect to login
                            successModal();
                        }
                    } else {
                        // If login fails, show success modal and redirect to login
                        successModal();
                    }
                } catch (loginError) {
                    console.error('Login after registration error:', loginError);
                    // Registration succeeded but login failed - show success and redirect to login
                    successModal();
                }
            } else {
                // Registration failed
                console.log("Registration error response", response)
                const errorMessage = getErrorMessage(response);
                errorModal(errorMessage);
            }

        } catch (error) {
            console.log("Registration error", error)
            const errorMessage = getErrorMessage(error);
            errorModal(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }

    const getErrorMessage = (error) => {
        if (!error) return 'An unknown error occurred';

        if (error.response?.data?.message) {
            return error.response.data.message;
        }

        return error.message || 'An error occurred during registration';
    };

    const errorModal = (errorMessage) => {
        modal.error({
            title: 'Registration Failed',
            content: `Please check your information and try again. ${errorMessage}`,
            className: 'register-modal',
            okText: 'Close'
        });
    };

    const successModal = () => {
        modal.success({
            title: 'Successfully Registered',
            content: 'You have successfully registered. You will be redirected to the login page.',
            className: 'register-modal',
            onOk() {
                window.location.href = '/login';
            }
        });
    }

    const emailAlreadyExistsModal = () => {
        modal.error({
            title: 'Error',
            content: 'The email address is already in use by another account, please try again with another email address.',
            className: 'register-modal'
        });
    }

    return (
        <div className='signup-page'
            style={{
                backgroundImage: `url(${signinBg})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
            }}
        >
            <section className="form">
                <div className="signin-top"
                    style={{
                        backgroundImage: `url(${signin})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center'
                    }}
                >
                    <h1 className="title">
                        Please fill the form to create your account
                    </h1>
                </div>
                <Spin spinning={isLoading}>
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                    >
                        <Form.Item
                            className='form-item'
                            name="name"
                            label="Name with Initials"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your name with initials!',
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || value.length <= 50) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Name should be less than 50 characters!'));
                                    },
                                }),
                                {
                                    pattern: /^[A-Za-z\s.]*$/,
                                    message: 'Please use only English characters!',
                                },
                            ]}
                        >
                            <Input placeholder="Enter your name with initials" />
                        </Form.Item>

                        <Form.Item
                            className='form-item'
                            label="Email Address"
                            name="email"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your Email address!',
                                },
                                {
                                    pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/,
                                    message: 'The input is not valid E-mail!',
                                }
                            ]}
                        >
                            <Input placeholder="sampleuser@gmail.com" />
                        </Form.Item>

                        <Form.Item
                            className='form-item'
                            name="password"
                            label="Password for your IBAS account"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your password',
                                },
                                { min: 6 },
                            ]}
                        >
                            <Input.Password
                                placeholder="Input password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>


                        <Form.Item
                            className='form-item'
                            label="Confirm Password"
                            name="conPassword"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please confirm your password!',
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('The new password that you entered do not match!'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                placeholder="Input password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <Form.Item
                            className='form-item'
                            label="Contact Number"
                            name="contactNumber"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your contact number!',
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || /^0[0-9]{9}$/.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('The input is not a valid phone number (e.g., 0XX XXXX XXX)!'));
                                    },
                                }),
                            ]}
                        >
                            <Input placeholder="0XX XXXX XXX" />
                        </Form.Item>

                        <Form.Item
                            className='form-item'
                            label="Address"
                            name="address"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input your address!',
                                },
                            ]}
                        >
                            <Input placeholder="Enter your address" />
                        </Form.Item>

                        {/* Center is fixed to IBAS (ID 2) */}
                        <Form.Item
                            name="centerId"
                            initialValue={2}
                            hidden
                        >
                            <Input type="hidden" />
                        </Form.Item>

                        <Form.Item className='form-bottom'>
                            <Button className='form-button' type="primary" htmlType="submit"
                            >
                                Register
                            </Button>

                            <div className="sign-up">
                                <p>Already have an account <Link to="/login">Sign in</Link></p>
                            </div>
                        </Form.Item>
                    </Form>
                </Spin>
            </section>
        </div>
    )
}

const Register = () => {
    return (
        <App>
            <RegisterContent />
        </App>
    )
}

export default Register